import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatSalaryGradeCodeLabelDisplay } from "@/shared/lib/format-salary-grade-display";
import { uploadAvatarFile } from "@/shared/lib/upload-avatar";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    FormInput,
    FormRoot,
    Skeleton,
    toast,
} from "@devhop/ui";
import {
    Banknote,
    Briefcase,
    Building2,
    ExternalLink,
    FileText,
    Hash,
    Home,
    Mail,
    MapPin,
    Phone,
    Save,
    User
} from "lucide-react";
import { useState } from "react";
import * as RHF from "react-hook-form";
import type { EmployeeExtendedInfoDTO } from "../../domain/contracts/employee-profile";
import { profileApi } from "../api/client";
import { useMyProfileQuery, usePatchMyProfileMutation } from "../api/queries";
import { useAuthState } from "../model/useAuthState";
import { ProfileForm } from "../ui/ProfileForm";

// ── Status labels ────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  active: "ຍັງເຮັດວຽກ",
  probation: "ທົດລອງງານ",
  probation_failed: "ບໍ່ຜ່ານທົດລອງ",
  resigned: "ລາອອກ",
  terminated: "ຖືກເລີກຈ້າງ",
};

const STATUS_VARIANT: Record<string, string> = {
  active: "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800",
  probation: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800",
  probation_failed: "text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800",
  resigned: "text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700",
  terminated: "text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800",
};

function InfoItem({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value ?? "—"}</p>
    </div>
  );
}

function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ── Address edit form ────────────────────────────────────────
type AddressFormValues = {
  currentVillage: string;
  currentDistrict: string;
  currentProvince: string;
  phoneNumber: string;
};

function AddressEditForm({ ext, phone }: { ext: EmployeeExtendedInfoDTO; phone: string | null }) {
  const patchMutation = usePatchMyProfileMutation();
  const methods = RHF.useForm<AddressFormValues>({
    values: {
      currentVillage: ext.currentVillage ?? "",
      currentDistrict: ext.currentDistrict ?? "",
      currentProvince: ext.currentProvince ?? "",
      phoneNumber: phone ?? "",
    },
  });

  function onSubmit(vals: AddressFormValues) {
    patchMutation.mutate(
      {
        currentVillage: vals.currentVillage.trim() || null,
        currentDistrict: vals.currentDistrict.trim() || null,
        currentProvince: vals.currentProvince.trim() || null,
        phoneNumber: vals.phoneNumber.trim() || null,
      },
      {
        onSuccess: () => toast.success("ບັນທຶກຂໍ້ມູນສຳເລັດ"),
        onError: () => toast.error("ບັນທຶກບໍ່ສຳເລັດ"),
      },
    );
  }

  return (
    <FormRoot<AddressFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormInput name="currentVillage" label="ບ້ານ" placeholder="ບ້ານ..." />
        <FormInput name="currentDistrict" label="ເມືອງ" placeholder="ເມືອງ..." />
        <FormInput name="currentProvince" label="ແຂວງ" placeholder="ແຂວງ..." />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput name="phoneNumber" label="ເບີໂທ" placeholder="020..." type="tel" />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={patchMutation.isPending} className="gap-2">
          <Save className="size-4" />
          {patchMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}

// ── Main Page ────────────────────────────────────────────────
export function ProfilePage() {
  const { user, refetch } = useAuthState();
  const [submitting, setSubmitting] = useState(false);
  const profileQuery = useMyProfileQuery();
  const profile = profileQuery.data;
  const ext = profile?.person.extended ?? null;

  const initials = ((user?.name ?? user?.email) || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const displayName = ext?.nameEn ?? ext?.nameLao ?? user?.name ?? "—";

  return (
    <>
      <Header />
      <Main>
        <div className="mb-4">
          <h2 className="font-bold text-2xl tracking-tight">ໂປຣໄຟລ໌ຂອງຂ້ອຍ</h2>
          <p className="text-muted-foreground text-sm">ຂໍ້ມູນສ່ວນຕົວ ແລະ ຈັດການບັນຊີ</p>
        </div>

        {/* ── Hero Card ── */}
        <Card className="mb-5">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-start gap-5">
              <Avatar className="size-20 shrink-0 ring-2 ring-border">
                <AvatarImage src={user?.image ?? undefined} />
                <AvatarFallback className="text-xl font-bold">{initials}</AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-bold text-xl leading-tight">{displayName}</h1>
                  {ext?.nameLao && ext?.nameEn && (
                    <span className="text-muted-foreground text-sm">({ext.nameLao})</span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  {ext?.employeeCode && (
                    <span className="flex items-center gap-1">
                      <Hash className="size-3.5" />
                      <span className="font-mono font-semibold text-foreground">{ext.employeeCode}</span>
                    </span>
                  )}
                  {ext?.branch && (
                    <Badge variant="outline" className="font-normal">{ext.branch}</Badge>
                  )}
                  {profile?.summary.positionTitle && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="size-3.5" />
                      {profile.summary.positionTitle}
                    </span>
                  )}
                  {profile?.summary.positionTitle && (
                    <span className="flex flex-wrap items-center gap-1 text-muted-foreground">
                      <span className="shrink-0">ລະດັບ:</span>
                      <span className="font-semibold text-foreground">
                        {formatSalaryGradeCodeLabelDisplay(
                          profile.summary.salaryGradeCode,
                          profile.summary.salaryGradeLabel,
                        )}
                      </span>
                    </span>
                  )}
                  {profile?.departmentName && (
                    <span className="flex items-center gap-1">
                      <Building2 className="size-3.5" />
                      {profile.departmentName}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {user?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="size-3.5" />
                      {user.email}
                    </span>
                  )}
                  {(ext?.currentVillage || user?.phoneNumber) && (
                    <span className="flex items-center gap-1">
                      <Phone className="size-3.5" />
                      {user?.phoneNumber ?? "—"}
                    </span>
                  )}
                </div>
              </div>

              {/* Status badge */}
              {ext && (
                <div className="shrink-0">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${STATUS_VARIANT[ext.status] ?? STATUS_VARIANT.active}`}>
                    <span className="size-2 rounded-full bg-current opacity-70" />
                    {STATUS_LABEL[ext.status] ?? ext.status}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {profileQuery.isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {!profileQuery.isLoading && (
          <div className="space-y-5">
            {/* ── ข้อมูลส่วนตัว (read-only) ── */}
            {ext && (
              <SectionCard icon={<User className="size-4" />} title="ຂໍ້ມູນສ່ວນຕົວ">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                  <InfoItem
                    label="ວັນເກີດ"
                    value={ext.dateOfBirth ? formatDateLocal(ext.dateOfBirth) : null}
                  />
                  <InfoItem
                    label="ເພດ"
                    value={
                      ext.gender === "male" ? "ຊາຍ"
                      : ext.gender === "female" ? "ຍິງ"
                      : ext.gender === "other" ? "ອື່ນໆ"
                      : null
                    }
                  />
                  <InfoItem
                    label="ສະຖານະສົມລົດ"
                    value={
                      ext.maritalStatus === "single" ? "ໂສດ"
                      : ext.maritalStatus === "married" ? "ແຕ່ງງານ"
                      : ext.maritalStatus === "divorced" ? "ຢ່າຮ້າງ"
                      : ext.maritalStatus === "widowed" ? "ໝ້າຍ"
                      : null
                    }
                  />
                  <InfoItem label="ສັນຊາດ" value={ext.nationality} />
                  <InfoItem label="ຊົນເຜົ່າ" value={ext.ethnicity} />
                  <InfoItem label="ສາສະໜາ" value={ext.religion} />
                  <InfoItem label="ກຸ່ມເລືອດ" value={ext.bloodType} />
                  {(ext.birthVillage || ext.birthDistrict || ext.birthProvince) && (
                    <div className="col-span-2 space-y-0.5 sm:col-span-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                        <MapPin className="size-3" />
                        ທີ່ຢູ່ເກີດ
                      </p>
                      <p className="text-sm font-medium">
                        {[ext.birthVillage, ext.birthDistrict, ext.birthProvince].filter(Boolean).join(", ")}
                      </p>
                    </div>
                  )}
                  {/* File link */}
                  {ext.documentFileKey && (
                    <div className="col-span-2 sm:col-span-3">
                      <a
                        href={`${config.apiUrl}/files/${ext.documentFileKey}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                      >
                        <FileText className="size-4" />
                        ເປີດໄຟລ໌ເອກກະສານ
                        <ExternalLink className="size-3" />
                      </a>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* ── ที่อยู่ปัจจุบัน + เบอร์โทร (แก้ไขได้) ── */}
            <SectionCard icon={<Home className="size-4" />} title="ທີ່ຢູ່ປັດຈຸບັນ ແລະ ເບີໂທ">
              {ext ? (
                <AddressEditForm ext={ext} phone={user?.phoneNumber ?? null} />
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  ຍັງບໍ່ມີຂໍ້ມູນ — ຕິດຕໍ່ HR ເພື່ອເພີ່ມຂໍ້ມູນພະນັກງານ
                </p>
              )}
            </SectionCard>

            {/* ── ข้อมูลการจ้างงาน (read-only) ── */}
            {ext && (
              <SectionCard icon={<Briefcase className="size-4" />} title="ຂໍ້ມູນການຈ້າງງານ">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <InfoItem label="ລະຫັດພະນັກງານ" value={ext.employeeCode} />
                  <InfoItem label="ສາຂາ" value={ext.branch} />
                  <InfoItem label="ສະຖານະ" value={STATUS_LABEL[ext.status] ?? ext.status} />
                  <InfoItem
                    label="ວັນສິ້ນສຸດທົດລອງ"
                    value={ext.probationEndDate ? formatDateLocal(ext.probationEndDate) : null}
                  />
                  <InfoItem
                    label="ລະດັບເງິນເດືອນຕຳແໜ່ງ"
                    value={
                      profile?.summary.positionTitle
                        ? formatSalaryGradeCodeLabelDisplay(
                            profile.summary.salaryGradeCode,
                            profile.summary.salaryGradeLabel,
                          )
                        : null
                    }
                  />
                  {ext.resignationDate && (
                    <InfoItem label="ວັນທີລາອອກ" value={formatDateLocal(ext.resignationDate)} />
                  )}
                  {ext.notes && (
                    <div className="col-span-2 space-y-0.5">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ໝາຍເຫດ</p>
                      <p className="text-sm whitespace-pre-line">{ext.notes}</p>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* ── บัญชีธนาคาร (read-only) ── */}
            {ext && (ext.bankAccountName || ext.bankAccountNumber) && (
              <SectionCard icon={<Banknote className="size-4" />} title="ບັນຊີທະນາຄານ">
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <InfoItem label="ຊື່ບັນຊີທະນາຄານ" value={ext.bankAccountName} />
                  <InfoItem label="ເລກບັນຊີທະນາຄານ" value={ext.bankAccountNumber} />
                </div>
              </SectionCard>
            )}

            {/* ── บัญชี (แก้ชื่อ/รูป/รหัสผ่าน) ── */}
            <SectionCard icon={<Mail className="size-4" />} title="ບັນຊີ">
              <div className="mb-4 text-sm text-muted-foreground">
                ອີເມວ: <span className="font-medium text-foreground">{user?.email}</span>
              </div>
              <ProfileForm
                initialValues={{ name: user?.name ?? "", image: user?.image ?? undefined }}
                submitting={submitting}
                onSubmit={async (vals) => {
                  setSubmitting(true);
                  try {
                    let imageKey: string | null | undefined = vals.image ?? undefined;
                    if (vals.imageFile instanceof File) {
                      imageKey = await uploadAvatarFile(vals.imageFile);
                    }
                    const fd = new FormData();
                    fd.append("name", vals.name);
                    if (vals.password) fd.append("password", vals.password);
                    if (vals.image === null && !vals.imageFile) {
                      fd.append("imageDelete", "1");
                    } else if (typeof imageKey === "string" && imageKey) {
                      fd.append("image", imageKey);
                    }
                    await profileApi.update(fd);
                    toast.success("ອັບເດດໂປຣໄຟລ໌ສໍາເລັດ");
                    refetch();
                  } finally {
                    setSubmitting(false);
                  }
                }}
              />
            </SectionCard>
          </div>
        )}
      </Main>
    </>
  );
}
